import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { FaLock, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ContestDetails from './ContestDetails';

interface HeroData {
  contest_id: number;
  contest_name: string;
  contest_date: string;
  contest_end_date: string;
  result_date: string | null;
  registration_opens_at: string;
  reward: string;
  reward_worth: number;
  no_of_questions: number;
  time_for_answer: number;
  entry_fee: string;
  total_slots: number;
  registration_count: number;
  day_name: string;
  has_attempted?: boolean;
  status: 'UPCOMING' | 'ACTIVE' | 'ENDED' | 'RESULT_DECLARED';
}

const HeroBanner: React.FC = () => {
  const [data, setData] = useState<HeroData | null>(null);
  const [timeLeft, setTimeLeft] = useState('Loading...');
  const [isRegisterLocked, setIsRegisterLocked] = useState(true);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [canWriteExam, setCanWriteExam] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<'REGISTRATION' | 'EXAM' | 'RESULT'>('REGISTRATION');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('authToken');

        const res = await fetch(`${API_BASE_URL}/hero-banner`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          setData(null);
          return;
        }

        const result = await res.json();
        setData(result);

        if (token) {
          const checkRes = await fetch(`${API_BASE_URL}/auth/check`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ contest_id: result.contest_id }),
          });
          const check = await checkRes.json();
          setAlreadyRegistered(check.alreadyRegistered);
        }
      } catch (err) {
        console.error('[HeroBanner] Error loading data:', err);
        setData(null);
        setTimeLeft('Error loading banner');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!data) return;

    const updateTimers = () => {
      const now = new Date().getTime();
      const regTime = new Date(data.registration_opens_at.replace(' ', 'T')).getTime();
      const contestStart = new Date(data.contest_date.replace(' ', 'T')).getTime();
      const contestEnd = new Date(data.contest_end_date.replace(' ', 'T')).getTime();
      const resultTime = data.result_date
        ? new Date(data.result_date.replace(' ', 'T')).getTime()
        : 0;

      // Determine current phase
      if (now < regTime) {
        // Registration not yet open
        setCurrentPhase('REGISTRATION');
        setIsRegisterLocked(true);
        const diff = regTime - now;
        setTimeLeft(formatCountdown(diff));
      } else if (now >= regTime && now < contestStart) {
        // Registration open, contest not started
        setCurrentPhase('REGISTRATION');
        setIsRegisterLocked(false);
        const diff = contestStart - now;
        setTimeLeft(`Registration open! Contest starts in ${formatCountdown(diff)}`);
      } else if (now >= contestStart && now < contestEnd) {
        // Contest running
        setCurrentPhase('EXAM');
        setIsRegisterLocked(true);
        setCanWriteExam(alreadyRegistered && !data.has_attempted);
        const diff = contestEnd - now;
        setTimeLeft(`Contest ends in ${formatCountdown(diff)}`);
      } else if (now >= contestEnd && (resultTime === 0 || now < resultTime)) {
        // Contest ended, results pending
        setCurrentPhase('RESULT');
        setIsRegisterLocked(true);
        setCanWriteExam(false);
        if (resultTime > 0) {
          const diff = resultTime - now;
          setTimeLeft(`Results in ${formatCountdown(diff)}`);
        } else {
          setTimeLeft('Results pending');
        }
      } else if (resultTime > 0 && now >= resultTime) {
        // Results declared
        setCurrentPhase('RESULT');
        setIsRegisterLocked(true);
        setCanWriteExam(false);
        setTimeLeft('Results declared!');
      }
    };

    // Format countdown helper
    const formatCountdown = (diff: number) => {
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    updateTimers();
    const interval = setInterval(updateTimers, 1000);

    return () => clearInterval(interval);
  }, [data, alreadyRegistered]);

  const handleRegisterClick = () => {
    const token = localStorage.getItem('authToken');
    if (token && data) {
      navigate('/contest-registration', { state: { contest: data } });
    } else {
      navigate('/login', { state: { from: '/contest-registration', contest: data } });
    }
  };

  const handleWriteExam = () => {
    navigate('/exam');
  };

  const progress = data ? Math.min((data.registration_count / data.total_slots) * 100, 100) : 0;

  if (!data) {
    return (
      <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-800 text-white pt-32 pb-16 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold">No contest is currently live.</h2>
        <p className="mt-2 text-sm opacity-80">Please check back later for upcoming contests.</p>
      </div>
    );
  }

  // Get appropriate timer icon based on phase
  const getTimerIcon = () => {
    switch (currentPhase) {
      case 'REGISTRATION':
        return '📅';
      case 'EXAM':
        return '⏳';
      case 'RESULT':
        return '🏆';
      default:
        return '⏱️';
    }
  };

  // Check if user missed the exam (registered but didn't attempt)
  const missedExam = alreadyRegistered && currentPhase === 'RESULT' && !data.has_attempted;

  if (missedExam) {
    return (
      <div className="w-full bg-gradient-to-r from-yellow-500 to-red-600 text-white pt-32 pb-16 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold">😢 Oops! You missed the exam.</h2>
        <p className="mt-2 text-sm opacity-90">Don't worry, try again in the next contest!</p>
      </div>
    );
  }
  const contestEndedForNonLoggedIn = !localStorage.getItem('authToken') &&
    currentPhase === 'RESULT' &&
    data &&
    new Date(data.contest_end_date.replace(' ', 'T')).getTime() < Date.now();

  if (missedExam) {
    return (
      <div className="w-full bg-gradient-to-r from-yellow-500 to-red-600 text-white pt-32 pb-16 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold">😢 Oops! You missed the exam.</h2>
        <p className="mt-2 text-sm opacity-90">Don't worry, try again in the next contest!</p>
      </div>
    );
  }

  if (contestEndedForNonLoggedIn) {
    return (
      <div className="w-full bg-gradient-to-r from-yellow-500 to-red-600 text-white pt-32 pb-16 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold">😢 Oops! You missed the contest.</h2>
        <p className="mt-2 text-sm opacity-90">Don't worry, try again in the next contest coming soon!</p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-800 text-white">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="flex flex-col lg:flex-row gap-10 lg:items-center w-full">
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4">
                Test Your <span className="block sm:inline">Aptitude,</span>{' '}
                <span className="block sm:inline">Win a {data.reward}!</span>
              </h1>
              <p className="text-lg mb-6 w-full max-w-full sm:max-w-md mx-auto lg:mx-0">
                Join our monthly contest for a chance to win a {data.reward} worth ₹{data.reward_worth}.<br />
                {currentPhase === 'REGISTRATION' && (
                  <span>Registration {isRegisterLocked ? 'opens' : 'is open'} this {data.day_name}!</span>
                )}
                {currentPhase === 'EXAM' && (
                  <span>The contest is currently running!</span>
                )}
                {currentPhase === 'RESULT' && (
                  <span>Stay tuned for results!</span>
                )}
              </p>

              {alreadyRegistered ? (
                <div className="mb-4">


                  {currentPhase === 'EXAM' && !data.has_attempted && (
                    <>
                      <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 border border-green-300 px-4 py-2 rounded-full shadow-sm text-sm font-medium">
                        🎉 You're already registered
                      </div>
                      <button
                        onClick={handleWriteExam}
                        className="relative group mt-4 w-full sm:w-auto px-6 py-3 font-semibold rounded-full flex items-center justify-center transition-all duration-300
        bg-gradient-to-r from-green-400 to-green-600 text-white shadow-md hover:from-green-500 hover:to-green-700"
                      >
                        <span className="relative z-10">📝 Write Exam</span>
                        <span className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-10 transition-all duration-300" />
                      </button>
                    </>
                  )}

                  {currentPhase === 'EXAM' && data.has_attempted && (
                    <div className="mt-4 text-sm text-white/80">
                      ✅ You've already attempted this contest
                    </div>
                  )}

                  {currentPhase === 'RESULT' && data.has_attempted && (
                    <div className="mt-4 text-sm text-white/80">
                      {timeLeft.includes('declared') ? (
                        'Check your results!'
                      ) : (
                        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 border border-green-300 px-4 py-2 rounded-full shadow-sm text-sm font-medium">
                          🎉 Thank you for taking the exam!
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <button
                    onClick={!isRegisterLocked ? handleRegisterClick : undefined}
                    disabled={isRegisterLocked}
                    className={`px-6 py-3 font-semibold rounded-lg transition flex items-center justify-center gap-2 
                      ${isRegisterLocked
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-white text-blue-600 hover:bg-gray-100'}`}
                  >
                    {isRegisterLocked ? <><FaLock className="text-sm" /> Locked</> : 'Register Now'}
                  </button>

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition flex items-center gap-2"
                  >
                    {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                    {showDetails ? 'Hide Details' : 'Learn More'}
                  </button>
                </div>
              )}
            </div>

            <div className="w-full lg:w-1/2">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20 w-full max-w-md mx-auto lg:mx-0">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xl">{getTimerIcon()}</span>
                  <span className="font-medium">
                    {currentPhase === 'REGISTRATION' && 'Registration Status'}
                    {currentPhase === 'EXAM' && 'Contest Status'}
                    {currentPhase === 'RESULT' && 'Results Status'}
                  </span>
                </div>
                <div className="text-xl font-semibold mb-4 text-center lg:text-left bg-white bg-opacity-20 px-4 py-2 rounded-md ring-1 ring-white ring-opacity-30 inline-block">
                  {timeLeft}
                </div>

                {currentPhase === 'REGISTRATION' && (
                  <>
                    <div className="w-full bg-white bg-opacity-20 rounded-full h-3 mb-2">
                      <div
                        className="bg-yellow-400 h-full rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>{data.registration_count} registered</span>
                      <span>{data.total_slots} slots</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`overflow-hidden transition-all duration-500 ${showDetails ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'} bg-white`}>
        <div className="container mx-auto px-4 py-8 md:py-12">
          {data && <ContestDetails contestData={data} />}
        </div>
      </div>
    </>
  );
};

export default HeroBanner;